/**
 * Authentication storage utilities.
 *
 * Handles persistence of authentication state in localStorage.
 */

import type { AuthUser } from '../types';

const AUTH_STORAGE_KEY = 'auth_user';
const TOKEN_STORAGE_KEY = 'auth_token';

/**
 * Query keys for authentication-related React Query queries.
 */
export const authQueryKeys = {
  currentUser: () => ['auth', 'currentUser'] as const,
};

/**
 * Get stored user data from localStorage.
 *
 * @returns AuthUser if found, null otherwise
 */
export function getStoredUser(): AuthUser | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedUser = localStorage.getItem(AUTH_STORAGE_KEY);
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);

    if (!storedUser || !storedToken) {
      return null;
    }

    return JSON.parse(storedUser) as AuthUser;
  } catch {
    return null;
  }
}

/**
 * Get stored authentication token from localStorage.
 *
 * @returns Token string if found, null otherwise
 */
export function getStoredToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Store token in localStorage.
 *
 * @param token - JWT access token
 */
export function storeToken(token: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch (error) {
    console.error('Failed to store token:', error);
  }
}

/**
 * Store user data in localStorage.
 *
 * @param user - Authenticated user data
 */
export function storeUser(user: AuthUser): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
  } catch (error) {
    console.error('Failed to store user data:', error);
  }
}

/**
 * Clear authentication data from localStorage.
 * Note: ETags are stored in React Query cache, which gets cleared on logout via queryClient.clear().
 */
export function clearAuth(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear auth data:', error);
  }
}
